import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Grid } from '@mui/material';
import { Button } from '@totalsoft/rocket-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import NotificationItemComponent from './NotificationItemComponent';
import { NotificationsBoxContainer } from './NotificationStyles';

const NotificationListComponent = ({
  notifications,
  handleMarkAsRead,
  handleDismiss,
  handleDismissAll,
  onErrorMessageChange,
  toggleNotificationModal,
  loadMoreNotifications
}) => {
  const { t } = useTranslation();

  const [isInfiniteLoading, setIsInfiniteLoading] = useState(false);
  const iScroll = useRef();

  const handleInfiniteLoad = useCallback(async () => {
    setIsInfiniteLoading(true);

    await loadMoreNotifications();

    setIsInfiniteLoading(false);
  }, [loadMoreNotifications]);

  const onScroll = useCallback(async () => {
    const notifierBox = iScroll.current;
    const notifierBoxRectangle = notifierBox.getBoundingClientRect();
    const lastItemRectangle = notifierBox.lastElementChild.getBoundingClientRect();

    if (!isInfiniteLoading && lastItemRectangle.top < notifierBoxRectangle.bottom) {
      await handleInfiniteLoad();
    }
  }, [handleInfiniteLoad, isInfiniteLoading]);

  useEffect(() => {
    let localIScroll = null;

    if (iScroll.current) {
      iScroll.current.addEventListener('scroll', onScroll);
      localIScroll = iScroll.current;
    }

    return () => {
      localIScroll.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  return (
    <Grid container>
      <Grid item xs={12} sx={{ textAlign: 'end', mb: 1 }}>
        <Button
          id={'offerHeader_notifications_dismissAllBtn'}
          color='primary'
          size='small'
          variant='text'
          onClick={handleDismissAll}
          sx={{ textTransform: 'none' }}
        >
          {t('Notifier.DismissAll')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        <NotificationsBoxContainer ref={iScroll}>
          {notifications.map((notification, idx) => {
            const text = notification.fromToast
              ? t(notification.notificationTitle, {
                  data: notification.notificationBody || notification.sourceContextKeys
                })
              : notification.notificationTitle;
            return (
              <NotificationItemComponent
                key={notification.notificationId}
                notification={notification}
                text={text}
                idx={idx}
                onMarkAsRead={handleMarkAsRead}
                onDismiss={handleDismiss}
                toggleNotificationModal={toggleNotificationModal}
                onErrorMessageChange={onErrorMessageChange}
              />
            );
          })}
          {isInfiniteLoading && <div>{t('Notifier.Loading')}</div>}
        </NotificationsBoxContainer>
      </Grid>
    </Grid>
  );
};

NotificationListComponent.propTypes = {
  notifications: PropTypes.array.isRequired,
  handleMarkAsRead: PropTypes.func.isRequired,
  handleDismiss: PropTypes.func.isRequired,
  handleDismissAll: PropTypes.func.isRequired,
  loadMoreNotifications: PropTypes.func.isRequired,
  toggleNotificationModal: PropTypes.func.isRequired,
  showNotificationModal: PropTypes.bool,
  onErrorMessageChange: PropTypes.func.isRequired
};

export default React.memo(NotificationListComponent);
